import { SpreadsheetSheet } from '@functions/export-spreadsheet';
import { displayDay } from './dashboard-filters';
import { CampaignReport, DashboardIdle, DashboardSummary, FunnelReport } from './services/responses/dashboard.response';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/// O painel inteiro em abas: o resumo e cada quebra da tela, com os filtros em vigor.
export const dashboardExportSheets = (
    summary: DashboardSummary,
    funnel: FunnelReport | undefined,
    idle: DashboardIdle | undefined,
    t: Translate,
): SpreadsheetSheet[] => {
    const { totals, previous } = summary;

    const sheets: SpreadsheetSheet[] = [
        {
            name: t('exportar.abaResumo'),
            rows: [
                [t('exportar.indicador'), t('exportar.valor'), t('painel.anterior')],
                [t('exportar.periodo'), `${displayDay(summary.from)} a ${displayDay(summary.to)}`,
                    `${displayDay(summary.previousFrom)} a ${displayDay(summary.previousTo)}`],
                [t('painel.leadsNovos'), totals.leads, previous.leads],
                [t('painel.vendas'), totals.won, previous.won],
                [t('painel.perdidos'), totals.lost, previous.lost],
                [t('crmDashboard.receita'), totals.wonValue, previous.wonValue],
                [t('painel.emNegociacao'), summary.openValue, null],
                [t('crmDashboard.semDono'), summary.unassignedOpen, null],
                ...(idle ? [[`${t('painel.filaTitulo')} · ${t('painel.faixaParado', { dias: idle.staleDays })}`, idle.stale, null]] : []),
            ],
        },
        {
            name: t('exportar.abaPorDia'),
            rows: [
                [t('exportar.dia'), t('exportar.qtdLeads'), t('painel.anterior')],
                ...totals.byDay.map((point, index) => [displayDay(point.day), point.count, previous.byDay[index]?.count ?? null]),
            ],
        },
        {
            name: t('exportar.abaOrigens'),
            rows: [[t('crmLista.origem'), t('exportar.qtdLeads')], ...summary.bySource.map((slice) => [slice.label, slice.count])],
        },
        {
            name: t('exportar.abaAtendentes'),
            rows: [
                [t('crmLista.responsavel'), t('exportar.qtdLeads'), t('exportar.ganhos'), t('exportar.interacoes'), t('exportar.acoesConcluidas')],
                ...summary.byAgent.map((agent) => [agent.name, agent.leads, agent.won, agent.interactions, agent.schedulesCompleted]),
            ],
        },
    ];

    if (idle) {
        sheets.push({
            name: t('painel.abaParados'),
            rows: [
                [t('crmLista.responsavel'), t('painel.faixaRecente', { dias: idle.agingDays }),
                    t('painel.faixaEsfriando', { de: idle.agingDays, ate: idle.staleDays }),
                    t('painel.faixaParado', { dias: idle.staleDays })],
                ...idle.byOwner.map((row) => [row.name, row.recent, row.aging, row.stale]),
            ],
        });
    }

    if (funnel) {
        sheets.push({
            name: t('exportar.abaFunil'),
            rows: [
                [t('crmLista.etapa'), t('exportar.passaram'), t('exportar.paradosAgora'), t('exportar.daEntrada'),
                    t('exportar.cairam'), t('exportar.taxaQueda')],
                ...funnel.stages.map((stage) => [
                    stage.name, stage.reached, stage.current, stage.shareOfEntry ?? null,
                    stage.droppedToNext ?? null, stage.dropRate ?? null,
                ]),
            ],
        });

        sheets.push({
            name: t('exportar.abaPerdas'),
            rows: [
                [t('exportar.motivoPerda'), t('exportar.qtdLeads'), t('exportar.valor')],
                ...funnel.lossReasons.map((reason) => [reason.name, reason.count, reason.value]),
            ],
        });
    }

    return sheets;
};

export const campaignExportRows = (report: CampaignReport, t: Translate) => [
    [
        t('exportar.campanha'), t('exportar.impressoes'), t('exportar.cliques'), t('exportar.gasto'), t('exportar.qtdLeads'),
        t('exportar.custoPorLead'), t('exportar.ganhos'), t('exportar.conversao'), t('exportar.custoPorGanho'),
        t('exportar.valorGanho'), t('exportar.roas'),
    ],
    ...report.campaigns.map((campaign) => [
        campaign.name, campaign.impressions, campaign.clicks, campaign.spend, campaign.leads,
        campaign.costPerLead ?? null, campaign.won, campaign.conversionRate ?? null, campaign.costPerWon ?? null,
        campaign.wonValue, campaign.roas ?? null,
    ]),
];
